import { lazy } from "react";
import Loadable from "components/Loadable";
import {
  routeConstants,
  FormatConstants,
  permissionConstants,
} from "services/keyConstants";
import * as url from "./urlHelper";
import { getStatusTypes } from "./service";
import { Description, Edit } from "@mui/icons-material";
import { getMasterDocDataByKey } from "services/ReduxUtils";
import {
  nonConformingProductList,
  nonConformingProductDownload,
} from "api/module/nonConformingProduct";

// Lazy-loaded components
const Report = Loadable(lazy(() => import("../Report")));
const CommonRecord = Loadable(lazy(() => import("../CommonRecord")));
const RemarksPage = Loadable(lazy(() => import("../Molecules/RemarksPage")));
const CommonDetail = Loadable(
  lazy(() => import("components/CommonDetail/CommonDetail")),
);

const CONCPRoutes = [
  {
    path: routeConstants.CONTROL_OF_NON_CONFIRMING,
    children: [
      {
        path: "detail/:id",
        element: (
          <CommonDetail
            navigateRoute={{
              isCondition: false,
              url: (item, filterData, defaultMasterDocData) => {
                const basePath = `/${routeConstants.CONTROL_OF_NON_CONFIRMING}/`;

                const isSpecialRecord = [
                  FormatConstants.NON_CONFORMING_OF_PRODUCT.REJECTION_RECORD,
                  FormatConstants.NON_CONFORMING_OF_PRODUCT.REWORK_RECORD,
                  FormatConstants.NON_CONFORMING_OF_PRODUCT.CONCESSION_RECORD,
                  FormatConstants.NON_CONFORMING_OF_PRODUCT.SCRAP_RECORD,
                ].includes(defaultMasterDocData?.defaultName);

                const isRegister = [
                  FormatConstants.NON_CONFORMING_OF_PRODUCT
                    .NON_CONFORMING_PRODUCT_REGISTER,
                  FormatConstants.NON_CONFORMING_OF_PRODUCT
                    .NON_CONFORMING_OF_PRODUCT,
                ].includes(defaultMasterDocData?.defaultName);

                return isSpecialRecord
                  ? `${basePath}record-type/${item?._id}`
                  : isRegister
                  ? `${basePath}report/${item?.mainDocumentId}/${item?.subDocumentId}`
                  : basePath;
              },
            }}
            isActiveWithoutPermission={(
              defaultMasterDocData,
            ) => {
              if (
                defaultMasterDocData?.defaultName ===
                FormatConstants.NON_CONFORMING_OF_PRODUCT
                  .NON_CONFORMING_PRODUCT_REGISTER
              ) {
                return true;
              }else{
                return true;
              }
            }}
            url={{
              GET: nonConformingProductList,
              DOWNLOAD: nonConformingProductDownload,
            }}
            getStatusTypes={getStatusTypes}
            filterOptions={{
              [FormatConstants.CUSTOMERSERVICE.CUSTOMER_SERVICING_DETAILS]: [
                {
                  name: "Status",
                  optionsKey: "statusOptions",
                  isHiddenAll: true,
                },
              ],
              [FormatConstants.NON_CONFORMING_OF_PRODUCT
                .NON_CONFORMING_PRODUCT_REGISTER]: [
                { name: "Status", optionsKey: "statusOptions" },
                { name: "Type", optionsKey: "typeOptionData" },
              ],
              [FormatConstants.NON_CONFORMING_OF_PRODUCT
                .NON_CONFORMING_OF_PRODUCT]: [
                { name: "Status", optionsKey: "statusOptions" },
                { name: "Type", optionsKey: "typeOptionData" },
              ],
              [FormatConstants.NON_CONFORMING_OF_PRODUCT.REJECTION_RECORD]: [
                { name: "Status", optionsKey: "statusOptions" },
              ],
              [FormatConstants.NON_CONFORMING_OF_PRODUCT.REWORK_RECORD]: [
                { name: "Status", optionsKey: "statusOptions" },
              ],
              [FormatConstants.NON_CONFORMING_OF_PRODUCT.RETURN_RECORD]: [
                { name: "Status", optionsKey: "statusOptions" },
              ],
              [FormatConstants.NON_CONFORMING_OF_PRODUCT.CONCESSION_RECORD]: [
                { name: "Status", optionsKey: "statusOptions" },
              ],
              [FormatConstants.NON_CONFORMING_OF_PRODUCT.SCRAP_RECORD]: [
                { name: "Status", optionsKey: "statusOptions" },
              ],
            }}
            /** 🔹 DOWNLOAD CONFIG */
            downloadConfig={(defaultMasterDocData, item, filterData) => {
              if (
                defaultMasterDocData?.defaultName ===
                FormatConstants.NON_CONFORMING_OF_PRODUCT
                  .NON_CONFORMING_PRODUCT_REGISTER
              ) {
                return {
                  _id: filterData?.version?.value,
                  type: filterData?.type,
                };
              } else if (
                defaultMasterDocData?.defaultName ===
                FormatConstants.NON_CONFORMING_OF_PRODUCT
                  .NON_CONFORMING_OF_PRODUCT
              ) {
                return { _id: item?.subDocumentId };
              }
              return {};
            }}
            buttonsData={(defaultMasterDocData) => {
              if (
                defaultMasterDocData?.defaultName ===
                FormatConstants.NON_CONFORMING_OF_PRODUCT
                  .NON_CONFORMING_PRODUCT_REGISTER
              ) {
                return [
                  {
                    name: "Download",
                    existingFunction: "handleDownload",
                  },
                ];
              }
              return null;
            }}
            tableConfig={(item, filterData, userData) => {
              return {
                defaultActions: [
                  FormatConstants.NON_CONFORMING_OF_PRODUCT.REJECTION_RECORD,
                  FormatConstants.NON_CONFORMING_OF_PRODUCT.REWORK_RECORD,
                  FormatConstants.NON_CONFORMING_OF_PRODUCT.CONCESSION_RECORD,
                  FormatConstants.NON_CONFORMING_OF_PRODUCT.SCRAP_RECORD,
                ].includes(item?.defaultName)
                  ? [
                      {
                        type: "view",
                        tooltip: "Go To Report",
                        color: "primary",
                        icon: <Description />,
                        key: "report",
                        auditTrail: {
                          masterDocData: getMasterDocDataByKey({
                            defaultName:
                              FormatConstants.CONTROL_OF_NON_CONFIRMING_PRODUCT
                                .REPORT,
                          }),
                        },
                        disabledObject: {
                          defaultName: item?.defaultName,
                          permissionKey: permissionConstants.UPDATE,
                        },
                        isCustomRoute: true,
                        getRoute: (row) =>
                          `../report/${row?.mainDocumentId}/${row?.subDocumentId}`,
                      },
                    ]
                  : FormatConstants.NON_CONFORMING_OF_PRODUCT
                      .NON_CONFORMING_PRODUCT_REGISTER
                  ? [
                      {
                        type: "view",
                        tooltip: "Update Remarks",
                        color: "primary",
                        icon: <Edit />,
                        key: "report",
                        disabledObject: {
                          defaultName: item?.defaultName,
                          permissionKey: permissionConstants.UPDATE,
                        },
                        isVisible: (row) => {
                          if (
                            row?.type === "In-coming Inspection" &&
                            row?.isRemarks === true
                          ) {
                            return true;
                          }

                          if (row?.isRemarks === false) {
                            return false;
                          }

                          return (
                            row?.type === "In-coming Inspection" &&
                            row?.responsibileUser?.some((user) => {
                              return (
                                user?.designationId ===
                                  userData?.designationId &&
                                user?.userId === userData?.userId
                              );
                            })
                          );
                        },
                        isCustomRoute: true,
                        getRoute: (row) => `../remarks/${row?.mainDocumentId}`,
                      },
                    ]
                  : undefined,
                chip: {
                  status: (data) => {
                    const color =
                      data?.row?.status === "Initiated" ? "primary" : "success";
                    return {
                      label: data?.row?.status,
                      color,
                    };
                  },
                },

                downloadActionConfig: {
                  isVisible: () => {
                    return item?.defaultName ===
                      FormatConstants.NON_CONFORMING_OF_PRODUCT
                        .NON_CONFORMING_PRODUCT_REGISTER
                      ? false
                      : true;
                  },
                },
              };
            }}
          />
        ),
      },
      {
        path: "report/:id/:childId",
        element: <Report />,
      },
      {
        path: "remarks/:id",
        element: <RemarksPage />,
      },
      {
        path: "record-type/:id",
        element: <CommonRecord />,
      },
    ],
  },
];

export default CONCPRoutes;
